import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { apiClient } from "@/api/client";
import type { operations } from "@/api/schema";

import { auditFilterToQuery, type AuditFilterState } from "../lib/url-state";

import { auditKeys } from "./query-keys";

type AuditExportQuery = NonNullable<operations["exportAuditEvents"]["parameters"]["query"]>;
type AuditExportJob =
  operations["getAuditExportJobStatus"]["responses"]["200"]["content"]["application/json"];

const TERMINAL_STATUSES: ReadonlySet<AuditExportJob["status"]> = new Set(["done", "failed"]);

// ponytail: fixed poll cadence, no backoff — export jobs are short-lived CSV
// dumps; revisit if a slower job type ever reuses this hook.
const DEFAULT_POLL_INTERVAL_MS = 2000;

/**
 * Builds the export operation's query from URL filter state. The list and
 * export endpoints share every filter field EXCEPT pagination: `exportAuditEvents`'s
 * query schema has no `offset`/`limit` (it exports everything matching the
 * filters, unpaginated) — so those two are destructured off and dropped
 * rather than spread through verbatim. `event_id` is already excluded by
 * `auditFilterToQuery` itself.
 */
function toExportQuery(filter: AuditFilterState): AuditExportQuery {
  const { offset: _offset, limit: _limit, group: _group, ...rest } = auditFilterToQuery(filter);
  // `group` is list-only — a CSV is always raw rows. `result` (comma list)
  // and `related_to` are shared with the list endpoint, so the export
  // matches exactly what is on screen.
  return rest;
}

/**
 * Kicks off an async CSV export job (POST /v1/audit-events/export) and polls
 * its status (GET /v1/audit-events/export/{job_id}) until it reaches a
 * terminal state (`done` or `failed`), then stops — never re-polls a
 * finished job, never gives up mid-flight on `queued`/`running`. Also stops
 * (and surfaces `pollError`) once the request itself hard-fails — network
 * error, 401/403/404, or 5xx past the global retry budget — instead of
 * polling forever against a job-status endpoint that never answers.
 */
export function useAuditExport(pollIntervalMs = DEFAULT_POLL_INTERVAL_MS) {
  const [jobId, setJobId] = useState<string | null>(null);

  const startExport = useMutation({
    mutationFn: async (filter: AuditFilterState) => {
      const query = toExportQuery(filter);
      const { data, error } = await apiClient.POST("/v1/audit-events/export", {
        params: { query },
      });
      /* v8 ignore next -- unreachable: client errorMiddleware throws ApiError on !ok */
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => setJobId(data.job_id),
  });

  const jobStatus = useQuery({
    queryKey: auditKeys.exportJob(jobId ?? ""),
    enabled: jobId !== null,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/audit-events/export/{job_id}", {
        params: { path: { job_id: jobId as string } },
      });
      /* v8 ignore next -- unreachable: client errorMiddleware throws ApiError on !ok */
      if (error) throw error;
      return data;
    },
    refetchInterval: (query) => {
      // query.state.status flips to "error" only once the global retry budget
      // (src/api/query-client.ts) is exhausted for a fetch — not per attempt
      // (query.state.fetchFailureCount resets every fetch, so it can't tell a
      // persistent 401/403/404, which the client never retries, from a fresh
      // poll). Stopping on "error" is what actually caps polling on a hard
      // failure instead of retrying the retry budget forever.
      if (query.state.status === "error") return false;
      const status = query.state.data?.status;
      return status !== undefined && TERMINAL_STATUSES.has(status) ? false : pollIntervalMs;
    },
  });

  return {
    startExport: startExport.mutate,
    startExportAsync: startExport.mutateAsync,
    isStarting: startExport.isPending,
    startError: startExport.error,
    job: jobStatus.data,
    isPolling: jobStatus.isFetching,
    pollError: jobStatus.error,
  };
}
